import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import api, { cachedGet } from '@/services/api';
import { GlobalCommandBar } from '@/components/GlobalCommandBar';
import GuidedTour, { isTourCompleted, markTourCompleted, getTourHighlightLabel } from '@/components/GuidedTour';
import { APP_VERSION } from '@/appVersion';
import {
  Gear,
  SignOut,
  List,
  Bell,
  MagnifyingGlass,
  Moon,
  Sun,
  X,
  House,
  ChatCircle,
  ChartBar,
  User,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Extracted configuration and components
import { navGroups, navItems } from '@/components/layout/navConfig';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import TopBar from '@/components/layout/TopBar';
import UserMenu from '@/components/layout/UserMenu';

export default function DashboardLayout() {
  const { user, logout, wsSubscribe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [badgeCounts, setBadgeCounts] = useState({ unread_inbox: 0, pending_followups: 0, call_requests: 0, pending_approvals: 0 });
  const [addonNavBadge, setAddonNavBadge] = useState(0);
  const [notificationsUnread, setNotificationsUnread] = useState(0);
  const [notificationItems, setNotificationItems] = useState([]);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [dealerLogoUrl, setDealerLogoUrl] = useState('');
  const [pageTransitionOn, setPageTransitionOn] = useState(false);
  const [usageSnapshot, setUsageSnapshot] = useState({ leads: 0, deals: 0 });
  const [tourRunning, setTourRunning] = useState(false);
  const [tourHighlight, setTourHighlight] = useState(null);
  const role = user?.role || '';
  const canSeeApprovals = ['manager', 'dealer_admin', 'admin', 'super_admin'].includes(role);

  // Auto-start tour for first-time users (check both server flag and localStorage)
  useEffect(() => {
    if (user && !isTourCompleted(user)) {
      const t = setTimeout(() => setTourRunning(true), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  const handleReplayTour = useCallback(() => {
    localStorage.removeItem('lotmate_tour_completed');
    setTourRunning(true);
  }, []);

  const handleTourFinish = useCallback(() => {
    setTourRunning(false);
    setTourHighlight(null);
  }, []);

  // Listen for tour step changes to highlight matching sidebar item
  useEffect(() => {
    const handler = (e) => {
      const label = getTourHighlightLabel(e.detail);
      setTourHighlight(label);
    };
    window.addEventListener('lotmate-tour-step', handler);
    return () => window.removeEventListener('lotmate-tour-step', handler);
  }, []);

  // Fetch plan features for nav gating + dealer logo for custom branding
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const [planRes, profileRes] = await Promise.allSettled([
          api.get('/dealership/plan'),
          api.get('/dealership/profile'),
        ]);
        if (planRes.status === 'fulfilled') {
          setPlanFeatures(planRes.value.data?.plan?.features || {});
        }
        if (profileRes.status === 'fulfilled') {
          setDealerLogoUrl(profileRes.value.data?.logo_url || '');
        }
      } catch (e) {}
    };
    fetchPlan();
  }, []);

  const shouldShowNavItem = useCallback((item) => {
    // Hide AI Approvals unless manager/admin and there is work to do.
    if (item.path === '/approvals') {
      return canSeeApprovals && (badgeCounts.pending_approvals || 0) > 0;
    }
    const managerRoles = ['manager', 'dealer_admin', 'admin', 'super_admin'];
    if (item.managerOnly && !managerRoles.includes(role)) return false;
    const progressiveUnlocked = (usageSnapshot.leads || 0) >= 20 || (usageSnapshot.deals || 0) >= 3 || managerRoles.includes(role);
    if (item.advanced && !progressiveUnlocked) return false;
    if (!item.feature) return true;
    if (!planFeatures) return true;
    return planFeatures[item.feature] === true;
  }, [badgeCounts.pending_approvals, canSeeApprovals, planFeatures, role, usageSnapshot.deals, usageSnapshot.leads]);

  useEffect(() => {
    const loadUsageSnapshot = async () => {
      try {
        const data = await cachedGet('/dashboard/full', 60000).catch(() => null);
        setUsageSnapshot({
          leads: Number(data?.stats?.leads_this_month || 0),
          deals: Number(data?.stats?.deals_this_month || 0),
        });
      } catch {
        setUsageSnapshot({ leads: 0, deals: 0 });
      }
    };
    loadUsageSnapshot();
  }, []);

  // Fetch real badge counts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await api.get('/dashboard/badge-counts');
        setBadgeCounts(res.data);
      } catch (e) {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadAddonBadge = async () => {
      try {
        const myRes = await api.get('/addons/my-addons');
        const pending = myRes.data?.pending_requests || [];
        setAddonNavBadge(Array.isArray(pending) ? pending.length : 0);
      } catch {
        setAddonNavBadge(0);
      }
    };
    loadAddonBadge();
    const t = setInterval(loadAddonBadge, 60000);
    return () => clearInterval(t);
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      const [cRes, lRes] = await Promise.all([
        api.get('/notifications/count'),
        api.get('/notifications'),
      ]);
      setNotificationsUnread(cRes.data?.unread || 0);
      setNotificationItems(Array.isArray(lRes.data) ? lRes.data : []);
    } catch {
      setNotificationItems([]);
    }
  }, []);

  // Notifications unread count (for bell badge) + list for dropdown
  useEffect(() => {
    refreshNotifications();
    const interval = setInterval(refreshNotifications, 30000);
    return () => clearInterval(interval);
  }, [refreshNotifications]);

  useEffect(() => {
    if (!wsSubscribe) return undefined;
    const unsub = wsSubscribe('inventory_aging_alert', () => {
      refreshNotifications();
    });
    return unsub;
  }, [wsSubscribe, refreshNotifications]);

  // Smooth page transitions on route change
  useEffect(() => {
    setPageTransitionOn(false);
    const raf = requestAnimationFrame(() => setPageTransitionOn(true));
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  // Apply dark mode
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  // Load collapsed state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved) setSidebarCollapsed(JSON.parse(saved));
  }, []);

  const toggleCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  const renderNavItem = useCallback(({ path, icon: Icon, label, badge, badgeKey }) => {
    const badgeCount = badge
      ? (badgeKey === 'addons' ? addonNavBadge : label === 'Inbox' ? badgeCounts.unread_inbox : label === 'Action Center' ? (badgeCounts.action_center_total ?? badgeCounts.pending_followups) : label === 'Calls' ? badgeCounts.call_requests : label === 'AI Approvals' ? badgeCounts.pending_approvals : 0)
      : 0;
    const isTourTarget = tourHighlight && tourHighlight === label;
    const content = (
      <NavLink
        to={path}
        data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
        data-tour={label}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-200",
            sidebarCollapsed ? "justify-center px-2 py-2" : "px-3 py-2",
            isActive
              ? "bg-primary/10 text-primary border-l-2 border-primary"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground border-l-2 border-transparent",
            isTourTarget && "tour-highlight"
          )
        }
      >
        <Icon size={18} weight="bold" />
        {!sidebarCollapsed && <span className="flex-1">{label}</span>}
        {!sidebarCollapsed && badge && badgeCount > 0 && (
          <Badge className="h-5 min-w-[20px] px-1.5 text-[10px] bg-red-500">{badgeCount}</Badge>
        )}
      </NavLink>
    );

    if (sidebarCollapsed) {
      return (
        <Tooltip key={path} delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {label}
            {badge && badgeCount > 0 && <Badge className="h-4 text-[10px] bg-red-500">{badgeCount}</Badge>}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <React.Fragment key={path}>{content}</React.Fragment>;
  }, [sidebarCollapsed, badgeCounts, addonNavBadge, tourHighlight]);

  return (
    <TooltipProvider>
      <ImpersonationBanner />
      <div className="h-screen bg-background flex overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside className={cn(
          "hidden md:flex flex-col border-r border-border bg-card transition-all duration-300 relative",
          sidebarCollapsed ? "w-16" : "w-44"
        )}>
          {/* Logo toggles collapse */}
          <div className={cn(
            "flex items-center border-b border-border transition-all duration-300 overflow-hidden",
            sidebarCollapsed ? "justify-center px-1 py-0" : "px-1 py-0"
          )}>
            <button
              type="button"
              onClick={toggleCollapse}
              className={cn(
                "flex rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card min-w-0 hover:opacity-90 transition-opacity",
                sidebarCollapsed ? "justify-center p-0.5" : "w-full justify-start",
              )}
              aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
              aria-expanded={!sidebarCollapsed}
              data-testid="sidebar-logo-toggle"
            >
              <img
                src={(planFeatures?.custom_branding && dealerLogoUrl) ? dealerLogoUrl : '/logo.svg'}
                alt=""
                className="object-contain object-center shrink-0 min-w-0 max-w-full pointer-events-none"
                style={{
                  height: '120px',
                  width: 'auto',
                  maxHeight: '120px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  margin: '-20px -8px',
                }}
              />
            </button>
          </div>

          {/* Nav Items - Grouped */}
          <ScrollArea className="flex-1 py-2">
            <nav className={cn(sidebarCollapsed ? "px-2" : "px-3")}>
              {navGroups.map((group) => {
                const groupItems = group.items.filter(shouldShowNavItem);
                if (groupItems.length === 0) return null;
                return (
                  <div key={group.label} className="mb-4">
                    {!sidebarCollapsed && (
                      <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/60 px-3 mb-1.5">{group.label}</p>
                    )}
                    <div className="space-y-0.5">
                      {groupItems.map((item) => renderNavItem({ ...item, badgeKey: item.badgeKey }))}
                    </div>
                  </div>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className={cn("p-3 border-t border-border", sidebarCollapsed && "p-2")}>
            <UserMenu
              sidebarCollapsed={sidebarCollapsed}
              initials={initials}
              user={user}
              navigate={navigate}
              handleLogout={handleLogout}
            />
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside className={cn(
          "fixed inset-y-0 left-0 w-44 max-w-[85vw] flex flex-col border-r border-border bg-card z-50 transform transition-transform md:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="flex items-center justify-between gap-2 px-1 py-0 border-b border-border overflow-hidden">
            <div className="flex items-center min-w-0 flex-1">
              <img
                src={(planFeatures?.custom_branding && dealerLogoUrl) ? dealerLogoUrl : '/logo.svg'}
                alt="LotMate"
                className="max-w-full object-contain object-center"
                style={{ height: '120px', width: 'auto', maxHeight: '120px', maxWidth: '100%', objectFit: 'contain', margin: '-20px -8px' }}
              />
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-1 rounded hover:bg-muted">
              <X size={20} />
            </button>
          </div>

          <ScrollArea className="flex-1 py-2">
            <nav className="px-3">
              {navGroups.map((group) => {
                const groupItems = group.items.filter(shouldShowNavItem);
                if (groupItems.length === 0) return null;
                return (
                  <div key={group.label} className="mb-4">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/60 px-3 mb-1.5">{group.label}</p>
                    <div className="space-y-0.5">
                      {groupItems.map(({ path, icon: Icon, label, badge, badgeKey }) => {
                        const badgeCount = badge
                          ? (badgeKey === 'addons' ? addonNavBadge : label === 'Inbox' ? badgeCounts.unread_inbox : label === 'Action Center' ? (badgeCounts.action_center_total ?? badgeCounts.pending_followups) : label === 'Calls' ? badgeCounts.call_requests : label === 'AI Approvals' ? badgeCounts.pending_approvals : 0)
                          : 0;
                        return (
                          <NavLink key={path} to={path} onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) => cn(
                              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                              isActive ? "bg-primary/10 text-primary border-l-2 border-primary" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground border-l-2 border-transparent"
                            )}>
                            <Icon size={18} weight="bold" />
                            <span className="flex-1">{label}</span>
                            {badge && badgeCount > 0 && <Badge className="h-5 text-[10px] bg-red-500">{badgeCount}</Badge>}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </nav>
          </ScrollArea>

          <div className="p-4 border-t border-border">
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-destructive hover:bg-muted transition-colors"
            >
              <SignOut size={20} weight="bold" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Header Bar */}
          <TopBar
            setSidebarOpen={setSidebarOpen}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            notificationsUnread={notificationsUnread}
            badgeCounts={badgeCounts}
            notificationItems={notificationItems}
            navigate={navigate}
            initials={initials}
            refreshNotifications={refreshNotifications}
            onReplayTour={handleReplayTour}
          />

          {/* Page Content — extra bottom padding on mobile for tab bar */}
          <main className="flex-1 min-h-0 flex flex-col overflow-hidden pb-16 md:pb-0">
            <div className={cn("page-transition-enter flex-1 min-h-0 overflow-auto", pageTransitionOn && "page-transition-enter-active")}>
              <Outlet />
            </div>
          </main>

          {/* Mobile Bottom Tab Bar */}
          <MobileBottomTabs badgeCounts={badgeCounts} />
        </div>
        <GlobalCommandBar />
        <GuidedTour run={tourRunning} onFinish={handleTourFinish} />
      </div>
    </TooltipProvider>
  );
}

const MOBILE_TABS = [
  { path: '/dashboard', icon: House, label: 'Home' },
  { path: '/inbox', icon: ChatCircle, label: 'My Leads', badgeKey: 'unread_inbox' },
  { path: '/reports', icon: ChartBar, label: 'Stats' },
  { path: '/settings', icon: User, label: 'Me' },
];

function MobileBottomTabs({ badgeCounts }) {
  const location = useLocation();
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-card border-t border-border safe-area-pb" data-testid="mobile-bottom-tabs">
      <div className="flex items-center justify-around h-14">
        {MOBILE_TABS.map(({ path, icon: Icon, label, badgeKey }) => {
          const isActive = location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
          const count = badgeKey ? (badgeCounts[badgeKey] || 0) : 0;
          return (
            <NavLink key={path} to={path} className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative">
              <div className="relative">
                <Icon size={22} weight={isActive ? 'fill' : 'regular'} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                {count > 0 && (
                  <span className="absolute -top-1 -right-2 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{count > 9 ? '9+' : count}</span>
                )}
              </div>
              <span className={cn("text-[10px] font-medium", isActive ? 'text-primary' : 'text-muted-foreground')}>{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}